import {
  QueryFunctionContext,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  addSavedSearch,
  AddSaveSearchReqBody,
  deleteSavedSearch,
  getSavedSearch,
  propertySearch,
  SaveSearchResType,
} from '../services/api';

export const useAddSavedSearch = (
  sub: string,
  queryData: Omit<AddSaveSearchReqBody, 'searchName' | 'sub'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['saved-search', sub, 'add'],
    mutationFn: async (searchName: string) => {
      return addSavedSearch({ body: { ...queryData, searchName, sub } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['saved-search', sub],
      });
    },
  });
};

export const useGetSavedSearches = (sub: string) => {
  return useQuery({
    queryKey: ['saved-search', sub],
    enabled: !!sub,
    queryFn: async ({ signal }) => {
      return getSavedSearch({ signal, sub });
    },
  });
};

export const useGetSavedSearchesResults = (
  savedSearch: SaveSearchResType[]
) => {
  return useQueries({
    queries:
      savedSearch &&
      savedSearch.map((search) => ({
        enabled: !!search,
        queryKey: ['saved-results', search.searchName],
        queryFn: ({ signal }: QueryFunctionContext) => {
          return propertySearch({
            signal,
            body: search,
          });
        },
      })),
  });
};

export const useDeleteSavedSearch = (sub: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['saved-search', sub, 'delete'],
    mutationFn: async (searchName: string) => {
      return deleteSavedSearch({ sub, searchName });
    },
    onMutate: (searchName: string) => {
      queryClient.setQueryData<SaveSearchResType[]>(
        ['saved-search', sub],
        (old) => old?.filter((search) => search.searchName !== searchName)
      );
    },
  });
};
